/*
 * File: Api.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Handler for calls to the orders API, used by both dashboard orders display and the actual orders page
 */
import { Http } from "api/Http";
import DateFormatter from "../../utils/DateFormatter";
import { format } from "date-fns";
const Api = new Http("orders/1");

class OrdersApi {
  constructor(api) {
    this.api = api;
  }

  /**
   * Fetch list of orders from the api
   * @param {String} filters filter values to send to the API, appended directly to API URL
   * @return {Array} array of orders matching the filters
   */

  fetch = async (filters, limit = 25) => {
    const res = await this.api.get(`/orders/?limit=${limit}&`, filters);

    return res || {};
  };

  /**
   * Fetch the count of orders from the api
   *
   * @param {String} filters filter values to send to the API, appended directly to API URL
   * @return {Number} total number of orders matching the filters
   */
  fetchCount = async filters => {
    const res = await this.fetch(filters, 1);
    const count = Number(res.headers["x-total-count"]);
    return count || 0;
  };

  /**
   * Find an order record on the api by id
   *
   * @param {Number} ordersId
   * @return {Object}
   */
  find = async ordersId => {
    let res = await this.api.get("/orders/" + ordersId);

    if (res.data.due) {
      res.data.due = new Date(res.data.due);
    }

    // order has notes so format the date(s)
    if (res.data.orderNotes && Array.isArray(res.data.orderNotes)) {
      res.data.orderNotes.forEach(
        note => (note.created = new Date(note.created))
      );
    }

    if (res.data.statusUpdates && Array.isArray(res.data.statusUpdates)) {
      res.data.statusUpdates.forEach(statusUpdate => {
        statusUpdate.dateString = DateFormatter.mdy(statusUpdate.created);
        statusUpdate.timeString = format(statusUpdate.created, "hh:mm a");
      });
    }
    return res.data || {};
  };

  /**
   * Creates a new order
   *
   * @param {Object} order
   * @return {Object}
   */
  create = async order => {
    let res = await this.api.post("/orders", order);
    return res.data || {};
  };

  /**
   * Updates an existing order
   *
   * @param {Object} order
   * @return {Object}
   */
  update = async order => {
    let res = await this.api.patch("/orders/" + order.ordersId, order);
    return res.data || {};
  };

  /**
   * Fetch order types
   *
   * @return {array}
   */
  orderTypes = async () => {
    let res = await this.api.get("/orderTypes");
    return res.data || [];
  };

  /**
   * Fetch order statuses
   *
   * @return {Array}
   */
  orderStatuses = async () => {
    let res = await this.api.get("/orderStatuses");
    return res.data || [];
  };
}

const newApi = new OrdersApi(Api);
export default newApi;
